import React, { FC, useState } from 'react';
import s from './toysBlock.module.scss';
import { CartCheckoutRejectBody, CartInputBody, profile, toy } from '@specs/specs';
import { ToysSummary } from '@components/Pages/CartPage/Summary/ToysSummary/ToysSummary';
import { ToyCartItem } from '@components/Cards/ToyCartItem/ToyCartItem';
import dynamic from 'next/dynamic';
import { useBuyToy } from '@services/store/hooks/useBuyToy';
import { LinksEnum, PageQueries } from '@specs/navigation';
import { ToysBlockHeader } from '@components/Pages/CartPage/ToysBlock/ToysBlockHeader';
import { useInvalidate } from '@hooks/TRPC_Hooks/useInvalidate';
import { useRouter } from 'next/router';
import { useProfile } from '@hooks/TRPC_Hooks/useProfile';
import { addToysToCart, ICartItemToys } from '@services/store/helpers/cart';
const Modal = dynamic(import('@components/Modals/Modal').then(module => module.Modal), {
  ssr: false
});
const DeleteFromCart = dynamic(import('@components/Modals/Variants/DeleteFromCart/DeleteFromCart').then(module => module.DeleteFromCart), {
  ssr: false
});
const BuyToysInCart = dynamic(import('@components/Modals/Variants/BuyToysInCart/BuyToysInCart').then(module => module.BuyToysInCart), {
  ssr: false
});
const InsufficientBalance = dynamic(import('@components/Modals/Variants/InsufficientBalance/InsufficientBalance').then(module => module.InsufficientBalance), {
  ssr: false
});
const ToyIsUnavailableInCart = dynamic(import('@components/Modals/Variants/ToyIsUnavailableInCart/ToyIsUnavailableInCart').then(module => module.ToyIsUnavailableInCart), {
  ssr: false
});
export type ModalTypes = 'BuyToysInCart' | 'InsufficientBalance' | 'DeleteFromCart' | 'ToyIsUnavailableInCart';
interface Props {
  list: ICartItemToys[];
  setList: React.Dispatch<React.SetStateAction<ICartItemToys[]>>;
}
export const ToysBlock: FC<Props> = ({
  list,
  setList
}) => {
  const router = useRouter();
  const {
    updateStoreItemsData
  } = useInvalidate();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [toysToRemove, setToysToRemove] = useState<ICartItemToys[]>([]);
  const [toysToBuy, setToysToBuy] = useState<CartInputBody>([]);
  const [selectedFigures, setSelectedFigures] = useState(list);
  const [openedModalType, setOpenedModalType] = useState<ModalTypes | undefined>(undefined);
  const {
    data: profile
  } = useProfile();
  const {
    buyHandler: buyToysHandler
  } = useBuyToy({
    onSuccess: async (data, variables) => {
      const {
        status
      } = data;
      if (status === 'reject') {
        const body = data.data;
        showModal('ToyIsUnavailableInCart');
        handleRejectedMutation(body);
      } else {
        await handleSuccessMutation(variables.cart.map(({
          toyId
        }) => toyId.toString()));
      }
    }
  });
  const goToExplorePage = async (purchasedToysQuery: PageQueries['purchasedToysQuery']) => {
    await router.push({
      pathname: LinksEnum.EXPLORE,
      query: {
        purchasedToysQuery
      } satisfies PageQueries
    }, LinksEnum.EXPLORE);
  };
  const handleRejectedMutation = (body: CartCheckoutRejectBody | undefined) => {
    if (typeof body === 'undefined') return;
    const storeItemsIdsWithRejectedStatus = body.filter(({
      status
    }) => status !== 'available').flatMap(({
      storeItemId
    }) => storeItemId);

    // заполняем массив фигурок в корзине статусами с сервера
    setList(prev => prev.map(item => ({
      ...item,
      figures: item.figures.map(figure => ({
        ...figure,
        status: body.find(storeItem => storeItem.storeItemId === figure.storeItemId)?.status ?? figure.status
      }))
    })));

    // удаляем проблемные фигурки из выбранных в корзине
    if (!!storeItemsIdsWithRejectedStatus.length) {
      setSelectedFigures(prev => prev.map(item => ({
        ...item,
        figures: item.figures.filter(({
          storeItemId
        }) => !storeItemsIdsWithRejectedStatus.includes(storeItemId))
      })).filter(({
        figures
      }) => !!figures.length));
    }
  };
  const handleSuccessMutation = async (toyIds: string[]) => {
    // инвалидация store items
    toysToBuy.map(updateStoreItemsData);
    const selectedFiguresIds = toysToBuy.map(({
      toyId
    }) => toyId);
    // фильтрация массива фигурок. Удаляем купленные и обновляем значение куки "cartItems"
    const cartItems = list.filter(({
      toyId
    }) => !selectedFiguresIds.includes(toyId));
    addToysToCart(cartItems);
    await goToExplorePage(toyIds);
    setList(cartItems);
    // удаляем фигурки из выбранных
    setSelectedFigures([]);
  };
  const removeHandler = (toyId: toy['id']) => {
    setList(prev => {
      const data = prev.filter(listItem => listItem.toyId !== toyId);
      addToysToCart(data);
      return data;
    });
    const cartItem = selectedFigures.find(figure => figure.toyId === toyId);
    if (typeof cartItem === 'undefined') return;
    setSelectedFigures(prev => prev.filter(figure => figure.toyId !== toyId));
  };
  const onClose = () => {
    setIsOpenModal(false);
  };
  const showModal = (type: ModalTypes) => {
    setOpenedModalType(type);
    setIsOpenModal(true);
  };
  const isInsufficientBalance = ({
    balance,
    ruby
  }: Pick<profile, 'balance' | 'ruby'>) => {
    const flawGold = balance - goldToysCount;
    const flawRuby = ruby - rubyToysCount;
    const needGold = flawGold < 0;
    const needRuby = flawRuby < 0;
    return needGold || needRuby;
  };
  const buySelectedHandler = () => {
    if (!profile) return;
    if (isInsufficientBalance(profile)) return showModal('InsufficientBalance');
    setToysToBuy(selectedFigures.flatMap(({
      figures
    }) => figures));
    showModal('BuyToysInCart');
  };
  const buyOneHandler = (figure: ICartItemToys['figures'][0]) => {
    if (!profile) return;
    if (isInsufficientBalance(profile)) return showModal('InsufficientBalance');
    setToysToBuy([figure]);
    showModal('BuyToysInCart');
  };
  const selectedFiguresArray = selectedFigures.flatMap(({
    figures
  }) => figures);
  const {
    rubyToysCount,
    goldToysCount
  } = selectedFiguresArray.reduce((acc, {
    currency,
    price
  }) => {
    if (currency === 'RUBY') acc.rubyToysCount += price;
    if (currency === 'GOLD') acc.goldToysCount += price;
    return acc;
  }, {
    rubyToysCount: 0,
    goldToysCount: 0
  });
  return <>
            <div className={s.cartBlock}>
                <div className={s.cartBlock__items}>
                    {list.length > 1 && <ToysBlockHeader list={list} selectedFigures={selectedFigures} setSelectedFigures={setSelectedFigures} deleteSelectedHandler={() => {
          setToysToRemove(selectedFigures);
          showModal('DeleteFromCart');
        }} />}
                    <div className={s.cartBlock__list}>
                        {list.map(toyCartItem => <ToyCartItem key={toyCartItem.toyId} selectedFigures={selectedFigures} setSelectedFigures={setSelectedFigures} toyCartItem={toyCartItem} deleteHandler={() => {
            setToysToRemove([toyCartItem]);
            showModal('DeleteFromCart');
          }} setList={setList} listLength={list.length} buyOneHandler={buyOneHandler} />)}
                    </div>
                </div>
                {!!selectedFiguresArray.length && profile && <ToysSummary buySelectedHandler={buySelectedHandler} goldToysCount={goldToysCount} rubyToysCount={rubyToysCount} />}
            </div>
            <Modal isOpen={isOpenModal} onClose={onClose} onAfterClose={() => {
      setToysToRemove([]);
      setToysToBuy([]);
      setOpenedModalType(undefined);
    }} data-sentry-element="Modal" data-sentry-source-file="ToysBlock.tsx">
                {openedModalType === 'DeleteFromCart' && <DeleteFromCart removeHandler={removeHandler} closeHandler={onClose} toysToRemove={toysToRemove} />}
                {openedModalType === 'BuyToysInCart' && <BuyToysInCart buyHandler={async () => {
        await buyToysHandler(toysToBuy);
      }} closeHandler={onClose} />}
                {openedModalType === 'InsufficientBalance' && profile && <InsufficientBalance closeHandler={onClose} flawRuby={profile.ruby - rubyToysCount} flawGold={profile.balance - goldToysCount} />}
                {openedModalType === 'ToyIsUnavailableInCart' && profile && <ToyIsUnavailableInCart closeHandler={onClose} />}
            </Modal>
        </>;
};